import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SEARCH_INDEX = [
  { path: '/', keys: ['nav.home'], tags: 'home main landing hero innovation semiconductor display chemical materials CRDMO CMO scale' },
  { path: '/about', keys: ['nav.about'], tags: 'about company who we are seoul korea technology innovation bridge CRDMO purification' },
  { path: '/ceo-message', keys: ['nav.ceoMessage'], tags: 'ceo message leadership vision founder' },
  { path: '/organisation-chart', keys: ['nav.orgChart'], tags: 'organisation organization chart team structure' },
  { path: '/company/events', keys: ['nav.events'], tags: 'events conferences exhibitions IMID deeptech 2025 2026 seoul busan display' },
  { path: '/services', keys: ['nav.services'], tags: 'services cmo crdmo manufacturing production materials solutions' },
  { path: '/services/integrated', keys: ['nav.integratedMaterials'], tags: 'integrated materials solutions CRDMO research development synthesis scale-up purification' },
  { path: '/services/production', keys: ['nav.specialtyProduction'], tags: 'specialty materials production CMO contract manufacturing high-purity' },
  { path: '/industries', keys: ['nav.industries'], tags: 'industries sectors semiconductor display personal care' },
  { path: '/industries/display', keys: ['nav.display'], tags: 'display OLED LCD next-generation materials' },
  { path: '/industries/semiconductor', keys: ['nav.semiconductor'], tags: 'semiconductor materials photoresist polymers advanced wafer' },
  { path: '/industries/personal-care', keys: ['nav.personalCare'], tags: 'personal care actives cosmetics ingredients sustainable' },
  { path: '/capabilities', keys: ['nav.capabilities'], tags: 'capabilities facilities expertise next-generation' },
  { path: '/capabilities/rd', keys: ['nav.rd'], tags: 'research development R&D lab laboratory synthesis analysis' },
  { path: '/capabilities/purifications', keys: ['nav.purification'], tags: 'purification technologies distillation sublimation recrystallization ultra-pure' },
  { path: '/capabilities/facility', keys: ['nav.facility'], tags: 'facility infrastructure cleanroom equipment reactor lab' },
  { path: '/capabilities/production-plants', keys: ['nav.productionPlants'], tags: 'production plants manufacturing india scale' },
  { path: '/brands', keys: ['nav.platformBrands'], tags: 'brands platform lumora IMID deeptech conference' },
  { path: '/get-in-touch', keys: ['nav.getStarted'], tags: 'contact get in touch inquiry email form requirements quote' }
];

export const SearchBar = ({ isMobile = false, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const hideTimer = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Click outside closes
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    setSelectedIndex(-1);
  }, []);

  const startHideTimer = useCallback(() => {
    hideTimer.current = setTimeout(close, 200);
  }, [close]);

  const cancelHideTimer = useCallback(() => {
    clearTimeout(hideTimer.current);
  }, []);

  const handleSearch = (val) => {
    setQuery(val);
    setSelectedIndex(-1);
    if (!val.trim()) { setResults([]); return; }
    const q = val.toLowerCase();
    setResults(
      SEARCH_INDEX.filter((item) => {
        const name = item.keys.map(k => t(k)).join(' ').toLowerCase();
        return name.includes(q) || item.tags.toLowerCase().includes(q);
      }).map((item) => ({ path: item.path, label: t(item.keys[0]) }))
    );
  };

  const go = (path) => { navigate(path); close(); if (onNavigate) onNavigate(); };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => Math.min(p + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(p => Math.max(p - 1, 0)); }
    else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) go(results[selectedIndex].path);
    else if (e.key === 'Escape') close();
  };

  /* ───── MOBILE ───── */
  if (isMobile) {
    return (
      <div ref={wrapperRef} className="w-full">
        {!isOpen ? (
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center space-x-2 px-4 py-3 w-full text-gray-200 hover:text-orange-400 hover:bg-gray-800 rounded-md"
            data-testid="mobile-search-toggle"
          >
            <Search size={18} />
            <span className="text-sm font-medium">Search</span>
          </button>
        ) : (
          <div className="px-2 py-2">
            <div className="relative">
              <div className="flex items-center bg-gray-800 rounded-lg border border-gray-700 focus-within:border-orange-500">
                <Search size={16} className="ml-3 text-gray-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search LAMKO materials..."
                  className="w-full bg-transparent text-white text-sm px-3 py-2.5 outline-none placeholder-gray-500"
                  data-testid="mobile-search-input"
                />
                <button onClick={close} className="mr-2 text-gray-400 hover:text-white"><X size={16} /></button>
              </div>
              {results.length > 0 && (
                <div className="mt-1 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden max-h-60 overflow-y-auto" data-testid="mobile-search-results">
                  {results.map((r, i) => (
                    <button key={r.path} onClick={() => go(r.path)}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${i === selectedIndex ? 'bg-orange-600 text-white' : 'text-gray-200 hover:bg-gray-700 hover:text-orange-400'}`}
                    >{r.label}</button>
                  ))}
                </div>
              )}
              {query && results.length === 0 && (
                <div className="mt-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-sm text-gray-500">No results found</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ───── DESKTOP ───── */
  return (
    <div
      ref={wrapperRef}
      style={{ width: 40, height: 40, position: 'relative' }}
      onMouseEnter={() => { cancelHideTimer(); setIsOpen(true); }}
      onMouseLeave={startHideTimer}
    >
      {/* Icon - always rendered but invisible when open */}
      <div
        style={{
          width: 40, height: 40,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          opacity: isOpen ? 0 : 1,
          pointerEvents: isOpen ? 'none' : 'auto'
        }}
        data-testid="desktop-search-toggle"
      >
        <Search size={22} color="#fff" />
      </div>

      {/* Expanded search - absolute, opens to the RIGHT */}
      {isOpen && (
        <div
          onMouseEnter={cancelHideTimer}
          onMouseLeave={startHideTimer}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 400,
            zIndex: 999
          }}
        >
          {/* Single input box */}
          <div style={{
            display: 'flex', alignItems: 'center', height: 40,
            background: '#1f2937', border: '1px solid #4b5563',
            borderRadius: 8
          }}>
            <Search size={16} style={{ marginLeft: 12, color: '#9ca3af', flexShrink: 0 }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search LAMKO materials..."
              style={{
                flex: 1, background: 'transparent', color: '#fff',
                fontSize: 14, padding: '0 12px', outline: 'none', border: 'none'
              }}
              data-testid="desktop-search-input"
            />
            {query && (
              <button onClick={() => { setQuery(''); setResults([]); inputRef.current?.focus(); }}
                style={{ marginRight: 8, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={14} />
              </button>
            )}
          </div>

          {/* Dropdown - strictly BELOW input */}
          {results.length > 0 && (
            <div
              data-testid="desktop-search-results"
              onMouseEnter={cancelHideTimer}
              onMouseLeave={startHideTimer}
              style={{
                marginTop: 4,
                background: '#fff', borderRadius: 8,
                boxShadow: '0 8px 30px rgba(0,0,0,.15)', border: '1px solid #e5e7eb',
                maxHeight: 320, overflowY: 'auto', zIndex: 999
              }}
            >
              {results.map((r, i) => (
                <button key={r.path} onClick={() => go(r.path)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    textAlign: 'left', padding: '10px 16px', fontSize: 14, cursor: 'pointer',
                    background: i === selectedIndex ? '#fff7ed' : '#fff',
                    color: i === selectedIndex ? '#ea580c' : '#374151',
                    border: 'none', borderBottom: '1px solid #f3f4f6'
                  }}
                  onMouseEnter={() => { cancelHideTimer(); setSelectedIndex(i); }}
                >
                  <Search size={14} color="#9ca3af" />
                  <span>{r.label}</span>
                </button>
              ))}
            </div>
          )}

          {query && results.length === 0 && (
            <div style={{
              marginTop: 4,
              background: '#fff', borderRadius: 8, padding: '10px 16px',
              boxShadow: '0 8px 30px rgba(0,0,0,.15)', border: '1px solid #e5e7eb',
              fontSize: 14, color: '#6b7280', zIndex: 999
            }}>
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};
